import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { FormProvider, useForm } from "react-hook-form";
import { PaziaPrefixInput } from "components/atoms/Inputs";
import { DUE_DATE_OPTIONS, DUE_DATE_OPTIONS_IDS } from "consts";
import {
  PaziaButton,
  PaziaDateSelect,
  PaziaInput,
  PaziaSelect,
  PaziaSwitch,
  PaziaTextarea,
} from "components/atoms";
import { useCourseQuery } from "../useCourseQuery";
import { useParams } from "react-router-dom";
import { IDropdownOption } from "components/atoms";
import { formatDate } from "utils";
import { CourseTrancheApi } from "api/types";
import { PaziaBox } from "components/atoms";

interface CreateTrancheFormProps {
  dateOfCourse: Date;
  onSubmit: (formData: TrancheFromValues) => Promise<void>;
  tranche?: CourseTrancheApi;
}

const TRANCHE_FORM_NAMES = {
  NAME: "name",
  PRICE: "price",
  DUEDATE: "dueDate",
  DUEDATEOPTIONS: "dueDateOptions",
  ISREQUIRED: "isRequired",
  DESCRIPTION: "description",
} as const;

const schema = yup
  .object({
    [TRANCHE_FORM_NAMES.NAME]: yup.string().required(),
    [TRANCHE_FORM_NAMES.PRICE]: yup
      .number()
      .typeError("Podaj cenę")
      .required("Podaj cenę")
      // A named test, not .min(): InputErrorMessage swaps "min" errors for a generic text
      .test("not-negative", "Cena transzy nie może być ujemna", (v) => v == null || v >= 0),
    [TRANCHE_FORM_NAMES.DUEDATE]: yup.date().required().nullable(),
    [TRANCHE_FORM_NAMES.ISREQUIRED]: yup.boolean().required(),
    [TRANCHE_FORM_NAMES.DUEDATEOPTIONS]: yup.string().required(),
    [TRANCHE_FORM_NAMES.DESCRIPTION]: yup.string(),
  })
  .required();

export type TrancheFromValues = yup.InferType<typeof schema>;

export const CreateTrancheForm = ({ onSubmit, dateOfCourse, tranche }: CreateTrancheFormProps) => {
  const { courseId } = useParams();
  const { course } = useCourseQuery(courseId);
  const methods = useForm<TrancheFromValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      isRequired: tranche?.isRequired ?? true,
      [TRANCHE_FORM_NAMES.NAME]: tranche?.name,
      [TRANCHE_FORM_NAMES.PRICE]: tranche?.price,
      [TRANCHE_FORM_NAMES.DUEDATE]: tranche?.dueDate ?? new Date(dateOfCourse),
      [TRANCHE_FORM_NAMES.DUEDATEOPTIONS]: tranche?.dueDate ? "-1" : DUE_DATE_OPTIONS_IDS.DAY_OF_COURSE,
      [TRANCHE_FORM_NAMES.DESCRIPTION]: tranche?.description,
    },
  });

  const { handleSubmit, watch, setValue, reset } = methods;

  const onDueDateChange = (dueDateSelected: IDropdownOption | null) => {
    let newDate: Date | null = new Date(dateOfCourse);
    switch (dueDateSelected?.value + "") {
      case DUE_DATE_OPTIONS_IDS.DAY_OF_COURSE:
        break;
      case DUE_DATE_OPTIONS_IDS.SELECT_DATE:
        // @ts-ignore
        setValue(TRANCHE_FORM_NAMES.DUEDATE, undefined);
        return;
      case DUE_DATE_OPTIONS_IDS.WEEK_BEFORE:
        newDate.setDate(newDate.getDate() - 7);
        break;
      case DUE_DATE_OPTIONS_IDS.TWO_WEEKS_BEFORE:
        newDate.setDate(newDate.getDate() - 14);
        break;
      case DUE_DATE_OPTIONS_IDS.MONTH_BEFORE:
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case DUE_DATE_OPTIONS_IDS.REGISTRATION_DAY:
        newDate = null;
        break;
    }
    setValue(TRANCHE_FORM_NAMES.DUEDATE, newDate);
  };

  const selectedDueDateOptions = watch(TRANCHE_FORM_NAMES.DUEDATEOPTIONS);
  const isRequired = watch(TRANCHE_FORM_NAMES.ISREQUIRED);
  const dueDate = watch(TRANCHE_FORM_NAMES.DUEDATE);
  const selectDate = selectedDueDateOptions === "-1";

  const onSubmitHandler = async (data: TrancheFromValues) => {
    await onSubmit(data);
    reset();
  };
  return (
    <FormProvider {...methods}>
      <PaziaBox>
        <form onSubmit={handleSubmit(onSubmitHandler)} className="grid gap-3">
          <PaziaInput name={TRANCHE_FORM_NAMES.NAME} label="Nazwa transzy" type="text" required />
          <PaziaPrefixInput
            name={TRANCHE_FORM_NAMES.PRICE}
            prefix="PLN"
            label="Cena bazowa"
            type="number"
            required
            step=".01"
          />
          <PaziaSelect
            label="Należy opłacić do"
            name={TRANCHE_FORM_NAMES.DUEDATEOPTIONS}
            options={DUE_DATE_OPTIONS}
            required
            onChange={onDueDateChange}
          />
          {selectDate ? (
            <PaziaDateSelect name={TRANCHE_FORM_NAMES.DUEDATE} range={[new Date(), course!.data.endOfCourse]} />
          ) : (
            selectedDueDateOptions !== undefined &&
            selectedDueDateOptions != DUE_DATE_OPTIONS_IDS.REGISTRATION_DAY && (
              <>
                <input
                  name="dueDatePrev"
                  className={`form-input h-[2.375rem] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 dark:disabled:border-slate-700 dark:disabled:bg-slate-700 dark:disabled:text-slate-600`}
                  disabled
                  value={dueDate ? formatDate(dueDate) : ""}
                />
                <PaziaInput name="dueDate" disabled hidden />
              </>
            )
          )}
          <div>
            <PaziaSwitch name={TRANCHE_FORM_NAMES.ISREQUIRED} label="Wymagane" />
          </div>

          {isRequired && (
            <div>
              <PaziaTextarea label="Opis" name={TRANCHE_FORM_NAMES.DESCRIPTION} />
            </div>
          )}
          <div className="flex justify-end">
            <PaziaButton type="success">{tranche ? "Zapisz zmiany" : "Dodaj transzę"}</PaziaButton>
          </div>
        </form>
      </PaziaBox>
    </FormProvider>
  );
};
